/**
 * Pop-up "mandar o próximo paciente entrar" na tela da RECEPÇÃO.
 *
 * Espelho INVERSO do [[NextPatientWatcher]] (recepção → médico): aqui o MÉDICO,
 * pelo topo da agenda lateral ([[DayAgendaDrawer]]), dispara um aviso GENÉRICO —
 * sem dizer QUEM é o próximo — pedindo que a recepção mande o próximo paciente
 * entrar. A recepção decide quem é pela ordem da fila. Componente global montado
 * no Shell — funciona em qualquer página em que a recepção esteja.
 *
 * Regras:
 *  - Só aparece para papéis de recepção (ADMIN / SECRETARIA / RECEPCIONISTA),
 *    mesma regra dos briefings / [[CallAlertWatcher]]. Médicos não veem.
 *  - Nunca mostra o aviso feito pelo PRÓPRIO usuário (`sentByUid`).
 *  - Respeita o escopo de agenda do perfil (Equipe → `allowedProfessionalIds`):
 *    recepção escopada só vê pedido dos médicos dela. Vazio = todos.
 *  - E o local de atendimento: pedido do médico atendendo em OUTRO lugar
 *    (com outra recepção) não pipoca aqui — ver utils/serviceLocal.
 *  - Avisos são efêmeros (RTDB, ver [[receptionAlert]]) e somem no "Ciente".
 *  - Ignora avisos antigos (> STALE_MS) pra não pipocar aviso velho ao abrir o
 *    sistema, e limpa os envelhecidos que ficaram sem "ciente". Toca o mesmo
 *    ding da chamada pra reforçar em qualquer tela.
 *  - Vários avisos viram uma fila: "Ciente" passa pro próximo.
 */
import { useEffect, useRef, useState } from 'react';
import { Megaphone, DoorOpen, X, Clock, User as UserIcon, Users } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { canSee } from '@/hooks/usePendencias';
import { isOtherLocal } from '@/utils/serviceLocal';
import { playCallChime } from '@/utils/chime';
import { ReceptionAlertService, type ReceptionAlertData } from '@/services/firebase/receptionAlert';

/** Avisos mais velhos que isso não pipocam e são limpos (ms). */
const STALE_MS = 5 * 60 * 1000;

function fmtTime(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export const CallNextWatcher = () => {
  const { clinicId, role, user, profile } = useAuth();
  const uid = user?.uid;
  // Chave estável pro effect (o array muda de identidade a cada render).
  const scopeKey = (profile?.allowedProfessionalIds ?? []).join(',');
  const myLocal = profile?.serviceLocal;
  // Fila local de avisos aguardando "ciência" da recepção (um de cada vez).
  const [pending, setPending] = useState<ReceptionAlertData[]>([]);
  // ids já vistos nesta sessão — só reage a avisos novos.
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!clinicId || !canSee(role, 'BRIEFING_NEEDED')) return;
    const scope = scopeKey ? scopeKey.split(',') : [];
    seenRef.current = new Set();
    const unsub = ReceptionAlertService.subscribe(clinicId, (alerts) => {
      const now = Date.now();
      // Limpa avisos velhos sem "ciente" (recepção estava offline) pra não
      // acumular lixo no RTDB.
      alerts
        .filter((a) => a.createdAt > 0 && now - a.createdAt >= STALE_MS)
        .forEach((a) => void ReceptionAlertService.dismiss(clinicId, a.id));
      const fresh = alerts.filter(
        (a) =>
          !seenRef.current.has(a.id) &&
          now - a.createdAt < STALE_MS &&
          // Defensivo: se o próprio usuário disparou (médico que também é
          // admin/recepção), não pipoca pra ele mesmo.
          a.sentByUid !== uid &&
          // Pedido de médico fora do escopo desta recepção não é com ela.
          (scope.length === 0 || scope.includes(a.sentByUid)) &&
          !isOtherLocal(myLocal, a.serviceLocal),
      );
      if (fresh.length === 0) return;
      fresh.forEach((a) => seenRef.current.add(a.id));
      setPending((q) => {
        const keys = new Set(q.map((p) => p.id));
        const add = fresh.filter((a) => !keys.has(a.id));
        return add.length ? [...q, ...add] : q;
      });
      playCallChime();
    });
    return () => unsub();
  }, [clinicId, role, uid, scopeKey, myLocal]);

  if (pending.length === 0) return null;

  const current = pending[0];
  const remaining = pending.length - 1;
  const ack = () => {
    if (clinicId) void ReceptionAlertService.dismiss(clinicId, current.id);
    setPending((q) => q.slice(1));
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 animate-fade-in"
      role="alertdialog"
      aria-modal="true"
      aria-label="Mandar próximo paciente entrar"
    >
      <div className="bg-surface rounded-2xl shadow-2xl border-2 border-primary/40 w-full max-w-md overflow-hidden">
        <div className="px-5 py-3 flex items-center gap-2 bg-primary text-on-primary">
          <Megaphone size={22} className="shrink-0" />
          <span className="font-headline font-bold text-lg">Próximo paciente</span>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <DoorOpen size={28} className="shrink-0 text-primary mt-0.5" />
            <p className="text-xl font-bold text-on-surface">
              Pode mandar o próximo paciente entrar.
            </p>
          </div>

          {/* Lembrete de acompanhante — o aviso é genérico (a recepção decide
              quem entra), então lembra de responder a pergunta / preencher o
              acompanhante do dia ANTES de mandar o paciente pro consultório. */}
          <div className="flex items-start gap-2 rounded-lg bg-amber/10 border border-amber/30 px-3 py-2">
            <Users size={16} className="shrink-0 text-amber mt-0.5" />
            <p className="text-sm text-on-surface">
              Lembre de preencher o <strong>acompanhante do dia</strong> do
              paciente que vai entrar (ou marcar que entra sozinho).
            </p>
          </div>

          {(current.room || current.sentByName) && (
            <div className="grid grid-cols-2 gap-3">
              {current.room && (
                <div className="flex items-center gap-2">
                  <DoorOpen size={18} className="shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Consultório</p>
                    <p className="text-on-surface font-medium truncate">{current.room}</p>
                  </div>
                </div>
              )}
              {current.sentByName && (
                <div className="flex items-center gap-2">
                  <UserIcon size={18} className="shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Pedido por</p>
                    <p className="text-on-surface font-medium truncate">{current.sentByName}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {current.createdAt > 0 && (
            <p className="text-xs text-on-surface-variant flex items-center gap-1">
              <Clock size={12} /> Pedido às {fmtTime(current.createdAt)}
            </p>
          )}

          {remaining > 0 && (
            <p className="text-xs font-bold text-primary">
              + {remaining} outro{remaining > 1 ? 's' : ''} aviso{remaining > 1 ? 's' : ''} na fila
            </p>
          )}

          <button
            onClick={ack}
            className="w-full py-3 rounded-xl font-bold text-base bg-primary text-on-primary hover:brightness-110 transition-all flex items-center justify-center gap-2"
          >
            <X size={18} /> Ciente
          </button>
        </div>
      </div>
    </div>
  );
};
